import React, { useState, useMemo, useCallback, useEffect } from 'react';
import * as XLSX from 'xlsx';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { parseISO } from 'date-fns';
import {
  Inbox,
  CheckCircle2,
  CalendarDays,
  Search,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Download,
  RefreshCw,
  SlidersHorizontal,
  Settings2,
  GripVertical,
  RotateCcw,
  Lock,
  Check,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multiselect';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useScreenData } from '@/hooks/useScreenData';
import { useScheduler } from '@/contexts/SchedulerContext';
import { useUser } from '@/contexts/UserContext';
import { useCompany } from '@/contexts/CompanyContext';
import { usePermissions } from '@/contexts/PermissionsContext';
import { useUnallocatedBookings, UnallocatedBooking } from '@/hooks/useUnallocatedBookings';
import { useLiveUnallocatedBookings } from '@/hooks/useLiveUnallocatedBookings';
import { UnallocatedTableRow } from '@/components/unallocated/UnallocatedTableRow';
import { AIBatchSuggestModal as BatchSuggestModal } from '@/components/unallocated/AIBatchSuggestModal';
import { ResourceRecommendationDialog } from '@/components/booking/ResourceRecommendationDialog';
import { DeleteBookingConfirmDialog } from '@/components/calendar/DeleteBookingConfirmDialog';
import { DeleteBookingsConfirmDialog } from '@/components/calendar/DeleteBookingsConfirmDialog';
import { DataModeToggle } from '@/components/DataModeToggle';
import { FeatureFeedbackCollector } from '@/components/FeedbackCollector';
import { HelpButton } from '@/components/help/HelpButton';
import { TablePagination, DEFAULT_PAGE_SIZE, paginate } from '@/components/TablePagination';
import {
  TABLE_CONTAINER_CLASS,
  TABLE_HEADER_ROW_CLASS,
  TABLE_HEAD_CLASS,
} from '@/lib/table-styles';
import { isAssignableResource } from '@/lib/resource-filters';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { mockAbsences, mockBankHolidays } from '@/lib/mock-data';
import { absencesApi } from '@/services/api';
import { buildBookingResourceMoveFields } from '@/utils/booking-resource-move';
import { canAssignBookingToResource } from '@/utils/resource-availability';
import type { Absence, Booking } from '@/types';

// ── Types ─────────────────────────────────────────────────────────────────────

type UrgencyFilter  = 'all' | 'critical' | 'soon' | 'upcoming';
type SortField      = 'urgency' | 'project' | 'date' | 'role' | 'skills' | 'location' | 'type' | 'bestMatch';
type SortDirection  = 'asc' | 'desc' | 'none';

// ── Column configuration ──────────────────────────────────────────────────────

export type ColumnKey = 'urgency' | 'booking' | 'date' | 'role' | 'skills' | 'location' | 'type' | 'bestMatch';
type ColumnCategory = 'Core' | 'Details' | 'Insights';

interface ColumnDef {
  key: ColumnKey;
  label: string;
  description: string;
  category: ColumnCategory;
  defaultVisible: boolean;
  locked?: boolean;
}

interface CategoryMeta {
  label: string;
  badgeAccent: 'teal' | 'purple' | 'info' | 'nile';
  badgeVariant: 'accent';
  description: string;
}

const CATEGORY_META: Record<ColumnCategory, CategoryMeta> = {
  Core:    { label: 'Core',    badgeAccent: 'nile',   badgeVariant: 'accent', description: 'Always-visible essential columns' },
  Details: { label: 'Details', badgeAccent: 'teal',   badgeVariant: 'accent', description: 'Resource and booking detail columns' },
  Insights: { label: 'Insights', badgeAccent: 'purple', badgeVariant: 'accent', description: 'Smart matching insights' },
};

const ALL_COLUMNS: ColumnDef[] = [
  { key: 'urgency',   label: 'Urgency',    description: 'Priority level and days until start',       category: 'Core',    defaultVisible: true,  locked: true },
  { key: 'booking',   label: 'Project',    description: 'Project name and customer',                 category: 'Core',    defaultVisible: true,  locked: true },
  { key: 'date',      label: 'Date',       description: 'Start and end date of the booking',         category: 'Core',    defaultVisible: true },
  { key: 'role',      label: 'Role',       description: 'Required role for the booking',             category: 'Details', defaultVisible: true },
  { key: 'skills',    label: 'Skills',     description: 'Required skills and competencies',          category: 'Details', defaultVisible: true },
  { key: 'location',  label: 'Location',   description: 'Work location or region',                   category: 'Details', defaultVisible: true },
  { key: 'type',      label: 'Type',       description: 'Booking type classification',               category: 'Details', defaultVisible: true },
  { key: 'bestMatch', label: 'Best Match', description: 'Smart-matched best-fit resources',          category: 'Insights', defaultVisible: true },
];

const DEFAULT_VISIBLE = new Set<ColumnKey>(ALL_COLUMNS.filter(c => c.defaultVisible).map(c => c.key));

interface UnallocatedBookingsProps {
  onNavigateToPlanner:   (bookingId?: string) => void;
}

// ── Urgency sort order ────────────────────────────────────────────────────────

const URGENCY_ORDER: Record<string, number> = { critical: 0, soon: 1, upcoming: 2 };

// ── Column header with sort ───────────────────────────────────────────────────

interface SortableHeadProps {
  field: SortField;
  label: string;
  sortField: SortField;
  sortDir: SortDirection;
  onSort: (f: SortField) => void;
  className?: string;
}

function SortableHead({ field, label, sortField, sortDir, onSort, className }: SortableHeadProps) {
  const active = sortField === field && sortDir !== 'none';
  return (
    <TableHead
      className={cn('cursor-pointer select-none group', TABLE_HEAD_CLASS, className)}
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        <span className={cn(
          'transition-colors',
          active
            ? 'text-foreground font-semibold'
            : 'text-muted-foreground group-hover:text-foreground',
        )}>
          {label}
        </span>
        {/* Active asc: up arrow. Active desc: down arrow. Inactive: ArrowUpDown on hover */}
        {active ? (
          sortDir === 'asc'
            ? <ArrowUp   className="h-3 w-3 text-primary shrink-0" />
            : <ArrowDown className="h-3 w-3 text-primary shrink-0" />
        ) : (
          <ArrowUpDown className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-60 transition-opacity shrink-0" />
        )}
      </div>
    </TableHead>
  );
}

// ── Sortable column row (drag-and-drop) ────────────────────────────────────────

interface SortableColumnRowProps {
  colDef: ColumnDef;
  isVisible: boolean;
  onToggle: (key: ColumnKey) => void;
}

function SortableColumnRow({ colDef, isVisible, onToggle }: SortableColumnRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: colDef.key });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    opacity: isDragging ? 0.85 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-3 px-4 py-3.5 rounded-xl border transition-colors duration-150',
        isDragging
          ? 'bg-accent border-primary/40 shadow-lg cursor-grabbing'
          : isVisible && !colDef.locked
            ? 'bg-card border-border hover:border-border/80 hover:bg-accent/30'
            : isVisible && colDef.locked
              ? 'bg-muted/20 border-border/40'
              : 'bg-muted/10 border-border/30',
      )}
    >
      {/* Drag handle */}
      <button
        className={cn(
          'shrink-0 text-muted-foreground/30 transition-colors touch-none',
          !colDef.locked && 'hover:text-muted-foreground cursor-grab active:cursor-grabbing',
          colDef.locked && 'cursor-default opacity-30',
        )}
        {...(!colDef.locked ? { ...attributes, ...listeners } : {})}
        aria-label={`Drag to reorder ${colDef.label}`}
        tabIndex={colDef.locked ? -1 : 0}
      >
        <GripVertical className="h-4 w-4" />
      </button>

      {/* Text */}
      <div className="flex-1 min-w-0">
        <p className={cn(
          'text-sm font-medium leading-snug',
          !isVisible ? 'text-muted-foreground' : 'text-foreground',
        )}>
          {colDef.label}
        </p>
        <p className="text-xs text-muted-foreground/60 mt-0.5 truncate leading-tight">
          {colDef.description}
        </p>
      </div>

      {/* Right-side controls — fixed-width slots so every row aligns perfectly */}
      <div className="shrink-0 flex items-center gap-3 ml-auto">

        {/* Category badge — fixed width container so badges don't shift the toggle */}
        <div className="w-16 flex justify-end">
          <Badge
            variant="accent"
            accent={
              colDef.category === 'Core' ? 'nile' :
              colDef.category === 'Insights' ? 'purple' : 'teal'
            }
            className="text-[10px] px-2 py-0 h-5 font-medium"
          >
            {colDef.category}
          </Badge>
        </div>

        {/* Lock OR Switch — fixed width so both land on the same column */}
        <div className="w-9 flex items-center justify-center">
          {colDef.locked ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="text-muted-foreground/40 cursor-default">
                  <Lock className="h-3.5 w-3.5" />
                </div>
              </TooltipTrigger>
              <TooltipContent side="left">
                <p className="text-xs">Always visible — required column</p>
              </TooltipContent>
            </Tooltip>
          ) : (
            <Switch
              checked={isVisible}
              onCheckedChange={() => onToggle(colDef.key)}
              aria-label={`${isVisible ? 'Hide' : 'Show'} ${colDef.label} column`}
            />
          )}
        </div>

      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

// ── localStorage helpers ────────────────────────────────────────────────────
const DEFAULT_ORDER = ALL_COLUMNS.map(c => c.key);

function loadColumnPrefs(userId: string): { visible: Set<ColumnKey>; order: ColumnKey[] } {
  try {
    const raw = localStorage.getItem(`unallocated-columns-${userId}`);
    if (!raw) return { visible: new Set(DEFAULT_VISIBLE), order: DEFAULT_ORDER };
    const parsed = JSON.parse(raw) as { visible?: string[]; order?: string[] };
    const validKeys = new Set(ALL_COLUMNS.map(c => c.key));
    const order = (parsed.order ?? DEFAULT_ORDER).filter((k): k is ColumnKey => validKeys.has(k as ColumnKey));
    // Add any newly added columns to the end if they're missing
    ALL_COLUMNS.forEach(c => { if (!order.includes(c.key)) order.push(c.key); });
    const visible = new Set<ColumnKey>(
      (parsed.visible ?? [...DEFAULT_VISIBLE]).filter((k): k is ColumnKey => validKeys.has(k as ColumnKey))
    );
    // Locked columns are always visible
    ALL_COLUMNS.filter(c => c.locked).forEach(c => visible.add(c.key));
    return { visible, order };
  } catch {
    return { visible: new Set(DEFAULT_VISIBLE), order: DEFAULT_ORDER };
  }
}

function saveColumnPrefs(userId: string, visible: Set<ColumnKey>, order: ColumnKey[]) {
  try {
    localStorage.setItem(`unallocated-columns-${userId}`, JSON.stringify({
      visible: Array.from(visible),
      order,
    }));
  } catch { /* storage full — silently ignore */ }
}

// ── Main component ────────────────────────────────────────────────────────────

export function UnallocatedBookings({ onNavigateToPlanner }: UnallocatedBookingsProps) {
  const { bookings: allBookings, resources, projects, settings, updateBooking, deleteBooking, deleteBookingsBatch, currentMode } = useScreenData('calendar');
  const { liveData } = useScheduler();
  const isLive = currentMode === 'live';
  const { user } = useUser();
  const { currentCompany } = useCompany();
  const { canDeleteBooking } = usePermissions();
  const userCanDeleteBooking = canDeleteBooking();

  // ── Live booking fetch ──────────────────────────────────────────────────────
  // In live mode the SchedulerContext's liveBookings is never bulk-fetched
  // (the calendar view uses useDateRangeBookings instead). The shared hook calls
  // the dedicated /bookings/unallocated endpoint so the API returns only bookings
  // where ResourceId IS NULL — no client-side filtering required — and reuses the
  // same request as the sidebar badge instead of issuing a second one. Opening
  // this page refetches once so the list is current.
  const {
    bookings: liveUnallocatedBookings,
    refresh: refreshUnallocated,
  } = useLiveUnallocatedBookings(currentCompany?.id, {
    enabled: isLive,
    refreshOnMount: true,
  });

  // ── Manual refresh (unallocated bookings only) ──────────────────────────────
  // Reloads in place: the existing list stays on screen until the new one lands,
  // and a failure leaves the current list untouched.
  const [isRefreshing, setIsRefreshing] = useState(false);
  const handleRefresh = useCallback(async () => {
    if (isRefreshing || !isLive || !currentCompany?.id) return;
    setIsRefreshing(true);
    try {
      await refreshUnallocated();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to refresh unallocated bookings');
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, isLive, currentCompany?.id, refreshUnallocated]);

  const unallocatedBookings = useUnallocatedBookings(
    isLive ? liveUnallocatedBookings : allBookings,
  );

  // Bookings that are already assigned — used as conflict-detection context for the recommendation engine
  const allocatedBookings = useMemo(
    () => allBookings.filter(b => b.resourceId !== 'unallocated'),
    [allBookings]
  );

  // ── Sort state ───────────────────────────────────────────────────────────────
  const [sortField, setSortField]   = useState<SortField>('date');
  const [sortDir, setSortDir]       = useState<SortDirection>('none');

  // ── Column visibility state — persisted to localStorage per user ─────────────
  const [visibleColumns, setVisibleColumns] = useState<Set<ColumnKey>>(() => loadColumnPrefs(user.id).visible);
  const [columnOrder, setColumnOrder]       = useState<ColumnKey[]>(() => loadColumnPrefs(user.id).order);
  const [configOpen, setConfigOpen]         = useState(false);

  // Persist to localStorage whenever column config changes
  useEffect(() => {
    saveColumnPrefs(user.id, visibleColumns, columnOrder);
  }, [user.id, visibleColumns, columnOrder]);

  const col = useCallback((key: ColumnKey) => visibleColumns.has(key), [visibleColumns]);

  const toggleColumn = (key: ColumnKey) => {
    const def = ALL_COLUMNS.find(c => c.key === key);
    if (def?.locked) return;
    setVisibleColumns(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const resetColumns = () => {
    setVisibleColumns(new Set(DEFAULT_VISIBLE));
    setColumnOrder(DEFAULT_ORDER);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setColumnOrder(prev => {
        const oldIdx = prev.indexOf(active.id as ColumnKey);
        const newIdx = prev.indexOf(over.id as ColumnKey);
        return arrayMove(prev, oldIdx, newIdx);
      });
    }
  };

  const dnsSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const visibleCount = ALL_COLUMNS.filter(c => visibleColumns.has(c.key)).length;

  // ── Filter state ────────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery]             = useState('');
  const [urgencyFilter, setUrgencyFilter]         = useState<UrgencyFilter>('all');
  const [bookingTypeFilter, setBookingTypeFilter] = useState<string>('all');
  const [projectFilter, setProjectFilter]         = useState<string>('all');
  const [roleFilter, setRoleFilter]               = useState<string>('all');
  const [skillsFilter, setSkillsFilter]           = useState<string[]>([]);

  // ── Pagination state ────────────────────────────────────────────────────────
  const [page, setPage]         = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  // ── Selection state ─────────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds]                       = useState<Set<string>>(new Set());
  const [singleSuggestOpen, setSingleSuggestOpen]           = useState(false);
  const [singleSuggestBooking, setSingleSuggestBooking]     = useState<UnallocatedBooking | null>(null);
  const [batchSuggestOpen, setBatchSuggestOpen]             = useState(false);

  // ── Delete confirmation state (mirrors the planner) ─────────────────────────
  const [bookingToDelete, setBookingToDelete]   = useState<Booking | null>(null);
  const [bookingsToDelete, setBookingsToDelete] = useState<Booking[]>([]);

  // ── Derived filter options ────────────────────────────────────────────────--
  const availableBookingTypes = useMemo(() => {
    const ids = new Set(unallocatedBookings.map(b => String(b.bookingTypeId)).filter(Boolean));
    return settings.bookingTypes.filter(bt => ids.has(String(bt.id)));
  }, [unallocatedBookings, settings.bookingTypes]);

  const availableProjects = useMemo(() => {
    const ids = new Set(unallocatedBookings.map(b => b.projectId ? String(b.projectId) : '').filter(Boolean));
    return projects.filter(p => ids.has(String(p.id)));
  }, [unallocatedBookings, projects]);

  const availableRoles = useMemo(() => {
    return Array.from(
      new Set(unallocatedBookings.map(b => b.requiredRole).filter((r): r is string => !!r))
    ).sort();
  }, [unallocatedBookings]);

  const availableSkills = useMemo(() => {
    const skillSet = new Set<string>();
    unallocatedBookings.forEach(b => b.requiredSkills?.forEach(s => skillSet.add(s)));
    return Array.from(skillSet).sort().map(s => ({ value: s, label: s }));
  }, [unallocatedBookings]);

  // ── Stats ───────────────────────────────────────────────────────────────────
  const stats = useMemo(() => ({
    total:    unallocatedBookings.length,
    critical: unallocatedBookings.filter(b => b.urgency === 'critical').length,
    soon:     unallocatedBookings.filter(b => b.urgency === 'soon').length,
    upcoming: unallocatedBookings.filter(b => b.urgency === 'upcoming').length,
  }), [unallocatedBookings]);

  // ── Filtered + sorted results ────────────────────────────────────────────────
  const filteredBookings = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    const filtered = unallocatedBookings.filter(b => {
      if (q) {
        const match =
          b.projectName.toLowerCase().includes(q) ||
          (b.customer?.toLowerCase().includes(q)         ?? false) ||
          (b.requiredRole?.toLowerCase().includes(q)     ?? false) ||
          (b.requiredSkills?.some(s => s.toLowerCase().includes(q)) ?? false) ||
          (b.location?.toLowerCase().includes(q)         ?? false);
        if (!match) return false;
      }
      if (urgencyFilter      !== 'all' && b.urgency               !== urgencyFilter)      return false;
      if (bookingTypeFilter  !== 'all' && String(b.bookingTypeId) !== bookingTypeFilter)  return false;
      if (projectFilter      !== 'all' && String(b.projectId)     !== projectFilter)      return false;
      if (roleFilter         !== 'all' && b.requiredRole          !== roleFilter)         return false;
      if (skillsFilter.length > 0 && !skillsFilter.every(s => b.requiredSkills?.includes(s))) return false;
      return true;
    });

    // When sort is cleared ('none') return insertion order (urgency natural order)
    if (sortDir === 'none') return filtered;

    return [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'urgency':
          cmp = (URGENCY_ORDER[a.urgency] ?? 99) - (URGENCY_ORDER[b.urgency] ?? 99);
          if (cmp === 0) cmp = a.daysUntilStart - b.daysUntilStart;
          break;
        case 'project':
          cmp = a.projectName.localeCompare(b.projectName);
          break;
        case 'date':
          cmp = a.daysUntilStart - b.daysUntilStart;
          break;
        case 'role':
          cmp = (a.requiredRole ?? '').localeCompare(b.requiredRole ?? '');
          break;
        case 'skills':
          cmp = (a.requiredSkills?.[0] ?? '').localeCompare(b.requiredSkills?.[0] ?? '');
          break;
        case 'location':
          cmp = (a.location ?? '').localeCompare(b.location ?? '');
          break;
        case 'type':
          cmp = String(a.bookingTypeId ?? '').localeCompare(String(b.bookingTypeId ?? ''));
          break;
        case 'bestMatch':
          // Best match has no numeric score in the data — fall back to urgency order
          cmp = (URGENCY_ORDER[a.urgency] ?? 99) - (URGENCY_ORDER[b.urgency] ?? 99);
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [unallocatedBookings, searchQuery, urgencyFilter, bookingTypeFilter, projectFilter, roleFilter, skillsFilter, sortField, sortDir]);

  const paginatedBookings = useMemo(
    () => paginate(filteredBookings, page, pageSize),
    [filteredBookings, page, pageSize],
  );

  // A new filter or sort order starts the reader back at the first page
  useEffect(() => {
    setPage(1);
  }, [searchQuery, urgencyFilter, bookingTypeFilter, projectFilter, roleFilter, skillsFilter, sortField, sortDir]);

  // Rows can disappear underneath the current page while it is being viewed
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredBookings.length / pageSize));
    if (page > totalPages) setPage(totalPages);
  }, [filteredBookings.length, page, pageSize]);

  // ── Active filter chips ─────────────────────────────────────────────────────
  const activeFilters = useMemo(() => {
    const chips: { key: string; label: string; clear: () => void }[] = [];
    if (searchQuery)
      chips.push({ key: 'search',   label: `"${searchQuery}"`,                        clear: () => setSearchQuery('') });
    if (urgencyFilter !== 'all')
      chips.push({ key: 'urgency',  label: `Urgency: ${urgencyFilter}`,               clear: () => setUrgencyFilter('all') });
    if (bookingTypeFilter !== 'all') {
      const bt = settings.bookingTypes.find(t => String(t.id) === bookingTypeFilter);
      chips.push({ key: 'type',     label: `Type: ${bt?.name ?? bookingTypeFilter}`,  clear: () => setBookingTypeFilter('all') });
    }
    if (projectFilter !== 'all') {
      const proj = projects.find(p => String(p.id) === projectFilter);
      chips.push({ key: 'project',  label: `Project: ${proj?.name ?? projectFilter}`, clear: () => setProjectFilter('all') });
    }
    if (roleFilter !== 'all')
      chips.push({ key: 'role',     label: `Role: ${roleFilter}`,                     clear: () => setRoleFilter('all') });
    skillsFilter.forEach(s =>
      chips.push({ key: `skill-${s}`, label: `Skill: ${s}`,                           clear: () => setSkillsFilter(prev => prev.filter(x => x !== s)) })
    );
    return chips;
  }, [searchQuery, urgencyFilter, bookingTypeFilter, projectFilter, roleFilter, skillsFilter, settings.bookingTypes, projects]);

  const clearAllFilters = () => {
    setPage(1);
    setSearchQuery('');
    setUrgencyFilter('all');
    setBookingTypeFilter('all');
    setProjectFilter('all');
    setRoleFilter('all');
    setSkillsFilter([]);
  };

  // ── Sort handler — 3-state: asc → desc → none ────────────────────────────────
  const handleSort = useCallback((field: SortField) => {
    setSortField(prev => {
      if (prev === field) {
        setSortDir(d => {
          if (d === 'asc')  return 'desc';
          if (d === 'desc') return 'none';
          return 'asc';
        });
        return prev;
      }
      setSortDir('asc');
      return field;
    });
  }, []);

  // ── Selection helpers ────────────────────────────────────────────────────────
  const toggleSelect = useCallback((bookingId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(bookingId)) next.delete(bookingId);
      else next.add(bookingId);
      return next;
    });
  }, []);

  const selectAll  = () => setSelectedIds(new Set(filteredBookings.map(b => b.id)));
  const clearAll   = () => setSelectedIds(new Set());
  const allSelected = filteredBookings.length > 0 && filteredBookings.every(b => selectedIds.has(b.id));

  // ── Availability ─────────────────────────────────────────────────────────────
  // Absences come from the backend in live mode, as they do on the planner.
  const [absences, setAbsences] = useState<Absence[]>(mockAbsences);
  useEffect(() => {
    if (!isLive) {
      setAbsences(mockAbsences);
      return;
    }
    if (!currentCompany?.id) return;
    const times = unallocatedBookings
      .flatMap(booking => [new Date(booking.startTime).getTime(), new Date(booking.endTime).getTime()])
      .filter(time => Number.isFinite(time));
    if (times.length === 0) return;

    let cancelled = false;
    absencesApi.getByDateRange(
      new Date(Math.min(...times)).toISOString(),
      new Date(Math.max(...times)).toISOString(),
      currentCompany.id,
    )
      .then(data => { if (!cancelled) setAbsences(data); })
      .catch(() => { if (!cancelled) setAbsences([]); });
    return () => { cancelled = true; };
  }, [isLive, currentCompany?.id, unallocatedBookings]);

  /**
   * The update that assigns a booking to a resource, or why the resource cannot
   * take it. The booking moves onto the resource's role and keeps the skills it
   * was booked with; availability is judged on the same working profile, bank
   * holiday, absence, clash and capacity conditions the planner enforces.
   */
  const buildAssignmentUpdate = useCallback((
    booking: Booking,
    resourceId: string,
  ): { update: Partial<Booking> } | { message: string } => {
    const resource = resources.find(r => String(r.id) === String(resourceId));
    if (!resource) return { update: { resourceId, status: 'confirmed' } };

    const availability = canAssignBookingToResource({
      resource,
      startTime: new Date(booking.startTime),
      endTime: new Date(booking.endTime),
      bookingId: String(booking.id),
      existingBookings: allBookings,
      globalWorkingProfile: settings.globalWorkingProfile,
      absences,
      bankHolidays: mockBankHolidays,
      businessHoursStart: settings.businessHoursStart,
      businessHoursEnd: settings.businessHoursEnd,
    });
    if (!availability.allowed) {
      return {
        message: availability.message ?? `${resource.name} cannot take this booking`,
      };
    }

    return {
      update: {
        ...buildBookingResourceMoveFields({ booking, targetResource: resource, isLive }),
        status: 'confirmed',
      },
    };
  }, [
    resources, allBookings, absences, isLive,
    settings.globalWorkingProfile, settings.businessHoursStart, settings.businessHoursEnd,
  ]);

  // ── Action handlers ──────────────────────────────────────────────────────────
  const handleSuggest = useCallback((booking: UnallocatedBooking) => {
    setSingleSuggestBooking(booking);
    setSingleSuggestOpen(true);
  }, []);

  const handleSingleConfirm = useCallback(async (
    resourceIds: string[],
    conflictOverrides: { conflictingBookingId: string; resourceId: string }[] = []
  ) => {
    if (!singleSuggestBooking || resourceIds.length === 0) return;
    const assignment = buildAssignmentUpdate(singleSuggestBooking, resourceIds[0]);
    if ('message' in assignment) {
      toast.error('Cannot assign resource', { description: assignment.message });
      return;
    }
    try {
      // Assign the selected resource to the unallocated booking
      await updateBooking(singleSuggestBooking.id, assignment.update);

      // For each overridden conflict, unallocate the booking that was displaced
      for (const override of conflictOverrides) {
        await updateBooking(override.conflictingBookingId, {
          resourceId: 'unallocated',
          status: 'planned',
        });
      }

      if (conflictOverrides.length > 0) {
        toast.success('Resource assigned with override', {
          description: `Booking confirmed. ${conflictOverrides.length} displaced booking${conflictOverrides.length !== 1 ? 's have' : ' has'} been moved to Unallocated.`,
        });
      } else {
        toast.success('Resource assigned', {
          description: 'Booking confirmed and assigned successfully.',
        });
      }
    } catch {
      toast.error('Failed to assign resource');
    }
  }, [singleSuggestBooking, updateBooking]);

  const handleBatchConfirm = useCallback(async (assignments: { bookingId: string; resourceId: string }[]) => {
    let successCount = 0;
    const rejected: string[] = [];
    for (const { bookingId, resourceId } of assignments) {
      const booking = unallocatedBookings.find(b => String(b.id) === String(bookingId));
      const assignment = booking
        ? buildAssignmentUpdate(booking, resourceId)
        : { update: { resourceId, status: 'confirmed' as const } };
      if ('message' in assignment) {
        rejected.push(assignment.message);
        continue;
      }
      try {
        await updateBooking(bookingId, assignment.update);
        successCount++;
      } catch { /* continue */ }
    }
    if (successCount > 0)
      toast.success(`${successCount} booking${successCount !== 1 ? 's' : ''} assigned`, {
        description: 'All assignable bookings have been confirmed and resourced.',
      });
    if (rejected.length > 0)
      toast.error(`${rejected.length} booking${rejected.length !== 1 ? 's' : ''} could not be assigned`, {
        description: rejected.join('\n'),
      });
    const failed = assignments.length - successCount - rejected.length;
    if (failed > 0)
      toast.error(`${failed} assignment(s) failed`);
  }, [updateBooking, unallocatedBookings, buildAssignmentUpdate]);

  // ── Delete handlers — same dialogs, toasts and API paths as the planner ─────
  const handleDeleteBooking = useCallback((booking: UnallocatedBooking) => {
    setBookingToDelete(booking);
  }, []);

  const handleCancelDelete = useCallback(() => setBookingToDelete(null), []);

  const handleConfirmDelete = useCallback(async () => {
    const target = bookingToDelete;
    setBookingToDelete(null);
    if (!target) return;

    try {
      await deleteBooking(String(target.id));
    } catch (err) {
      console.error(`Failed to delete booking ${target.id}:`, err);
      toast.error('Failed to delete booking');
      return;
    }

    setSelectedIds(prev => {
      if (!prev.has(String(target.id))) return prev;
      const next = new Set(prev);
      next.delete(String(target.id));
      return next;
    });

    toast.success('Booking Deleted', {
      description: 'The booking has been successfully deleted.',
    });

    if (isLive) void refreshUnallocated();
  }, [bookingToDelete, deleteBooking, isLive, refreshUnallocated]);

  const handleDeleteBookings = useCallback((selected: UnallocatedBooking[]) => {
    if (selected.length === 0) return;
    if (selected.length === 1) {
      handleDeleteBooking(selected[0]);
      return;
    }
    setBookingsToDelete(selected);
  }, [handleDeleteBooking]);

  const handleCancelBulkDelete = useCallback(() => setBookingsToDelete([]), []);

  const handleConfirmBulkDelete = useCallback(async () => {
    const targets = bookingsToDelete;
    setBookingsToDelete([]);
    if (targets.length === 0) return;

    let deleted: Array<string | number> = [];
    let errors: string[] = [];
    try {
      ({ deleted, errors } = await deleteBookingsBatch(targets.map(booking => booking.id)));
    } catch (err) {
      console.error('Failed to delete the selected bookings:', err);
      toast.error(`Failed to delete ${targets.length} booking${targets.length === 1 ? '' : 's'}`);
      return;
    }

    const deletedIds = new Set(deleted.map(id => String(id)));
    setSelectedIds(prev => {
      const next = new Set(prev);
      deletedIds.forEach(id => next.delete(id));
      return next;
    });

    if (deleted.length > 0) {
      toast.success('Bookings Deleted', {
        description: `${deleted.length} booking${deleted.length === 1 ? '' : 's'} ${deleted.length === 1 ? 'has' : 'have'} been deleted.`,
      });
    }
    if (errors.length > 0) {
      toast.error(`Failed to delete ${errors.length} booking${errors.length === 1 ? '' : 's'}`, {
        description: errors.join('\n'),
      });
    }

    if (isLive) void refreshUnallocated();
  }, [bookingsToDelete, deleteBookingsBatch, isLive, refreshUnallocated]);

  const handleViewInPlanner = useCallback((booking: UnallocatedBooking) => {
    onNavigateToPlanner(booking.id);
    toast.info('Opening Planner', { description: `Navigating to ${booking.projectName}` });
  }, [onNavigateToPlanner]);

  const handleExportToExcel = useCallback(() => {
    const rows = filteredBookings.map(b => {
      const project = projects.find(p => String(p.id) === String(b.projectId));
      const bt      = settings.bookingTypes.find(t => String(t.id) === String(b.bookingTypeId));
      return {
        'Urgency':       b.urgency.charAt(0).toUpperCase() + b.urgency.slice(1),
        'Project':       b.projectName,
        'Customer':      b.customer      ?? '',
        'Start Date':    b.startTime.slice(0, 10),
        'End Date':      b.endTime.slice(0, 10),
        'Days Until':    b.daysUntilStart,
        'Required Role': b.requiredRole  ?? '',
        'Skills':        (b.requiredSkills ?? []).join(', '),
        'Location':      b.location      ?? '',
        'Booking Type':  bt?.name        ?? '',
        'Project Code':  project?.code   ?? '',
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook  = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Unallocated Bookings');

    // Auto-size columns
    const colWidths = Object.keys(rows[0] ?? {}).map(key => ({
      wch: Math.max(key.length, ...rows.map(r => String(r[key as keyof typeof r]).length)) + 2,
    }));
    worksheet['!cols'] = colWidths;

    XLSX.writeFile(workbook, `unallocated-bookings-${new Date().toISOString().slice(0, 10)}.xlsx`);

    toast.success('Export complete', {
      description: `${filteredBookings.length} booking${filteredBookings.length !== 1 ? 's' : ''} exported to Excel.`,
    });
  }, [filteredBookings, projects, settings.bookingTypes]);

  const bookingsForBatch = useMemo(() => {
    if (selectedIds.size > 0) return filteredBookings.filter(b => selectedIds.has(b.id));
    return filteredBookings;
  }, [filteredBookings, selectedIds]);

  const singleSuggestStart = singleSuggestBooking ? parseISO(singleSuggestBooking.startTime) : undefined;
  const singleSuggestEnd   = singleSuggestBooking ? parseISO(singleSuggestBooking.endTime)   : undefined;

  // ── Empty state (all resourced) — now shown inline in tab ─────────────────

  // ── Main render ──────────────────────────────────────────────────────────────
  return (
    <div className="h-full overflow-y-auto overflow-x-hidden" data-tour="unallocated-list">
      <div className="w-full px-6 pt-5 pb-8 space-y-3">

        {/* ── Header ── */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 shrink-0">
              <Inbox className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-semibold leading-tight">Unallocated Bookings</h1>
              <p className="text-muted-foreground text-sm mt-0.5">Bookings in the scheduler that still need a resource assigned</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <DataModeToggle screen="calendar" />
            <FeatureFeedbackCollector feature="Open Demand" />
            <HelpButton />
          </div>
        </div>

        {/* ══ Unallocated Bookings Section ══ */}
        <div className="space-y-3">
          {/* Section toolbar */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2.5">
              <span className="text-sm font-semibold">Awaiting a resource</span>
              {stats.total > 0 && (
                <Badge variant="destructive" className="text-[10px] font-bold tabular-nums px-1.5 py-0 h-4">
                  {stats.total}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {userCanDeleteBooking && selectedIds.size > 0 && (
                <Button
                  size="sm"
                  variant="destructive"
                  className="gap-1.5"
                  onClick={() => handleDeleteBookings(filteredBookings.filter(b => selectedIds.has(b.id)))}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete ({selectedIds.size} selected)
                </Button>
              )}
              <Button
                size="sm"
                className="gap-1.5"
                onClick={() => setBatchSuggestOpen(true)}
                disabled={filteredBookings.length === 0}
              >
                Suggest Resources {selectedIds.size > 0 ? `(${selectedIds.size} selected)` : '— All'}
              </Button>
            </div>
          </div>

            {/* Empty state when all bookings are resourced */}
            {unallocatedBookings.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24 text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-green-500/10 mb-4">
                  <CheckCircle2 className="h-8 w-8 text-green-500" />
                </div>
                <h2 className="text-lg font-semibold">All demand is resourced!</h2>
                <p className="text-sm text-muted-foreground mt-1 max-w-xs">
                  There are no unallocated bookings waiting for a resource.
                </p>
                <Button variant="outline" size="sm" className="mt-4 gap-1.5" onClick={() => onNavigateToPlanner()}>
                  <CalendarDays className="h-4 w-4" />
                  Go to Planner
                </Button>
              </div>
            ) : (
              <>

        {/* ── Filter Panel ── */}
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            {/* Search */}
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                placeholder="Search by project, customer, role or skill…"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-9 h-9 bg-card"
              />
            </div>

            {/* Urgency */}
            <Select value={urgencyFilter} onValueChange={v => setUrgencyFilter(v as UrgencyFilter)}>
              <SelectTrigger className="h-9 w-[140px] text-sm bg-card">
                <SelectValue>
                  {urgencyFilter === 'all' && <span className="text-muted-foreground">All Urgency</span>}
                  {urgencyFilter === 'critical' && (
                    <span className="flex items-center gap-1.5">
                      <span className="h-2 w-2 rounded-full bg-destructive shrink-0" />
                      <span className="text-destructive font-medium">Critical</span>
                      <span className="text-muted-foreground tabular-nums">({stats.critical})</span>
                    </span>
                  )}
                  {urgencyFilter === 'soon' && (
                    <span className="flex items-center gap-1.5">
                      <span className="h-2 w-2 rounded-full bg-yellow-400 shrink-0" />
                      <span className="text-yellow-600 font-medium">Soon</span>
                      <span className="text-muted-foreground tabular-nums">({stats.soon})</span>
                    </span>
                  )}
                  {urgencyFilter === 'upcoming' && (
                    <span className="flex items-center gap-1.5">
                      <span className="h-2 w-2 rounded-full bg-primary/40 shrink-0" />
                      <span className="font-medium">Upcoming</span>
                      <span className="text-muted-foreground tabular-nums">({stats.upcoming})</span>
                    </span>
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Urgency</SelectItem>
                <SelectItem value="critical">
                  <span className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-destructive shrink-0" />
                    Critical <span className="text-muted-foreground tabular-nums">({stats.critical})</span>
                  </span>
                </SelectItem>
                <SelectItem value="soon">
                  <span className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-yellow-400 shrink-0" />
                    Soon <span className="text-muted-foreground tabular-nums">({stats.soon})</span>
                  </span>
                </SelectItem>
                <SelectItem value="upcoming">
                  <span className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-primary/40 shrink-0" />
                    Upcoming <span className="text-muted-foreground tabular-nums">({stats.upcoming})</span>
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>

            {/* Project */}
            {availableProjects.length > 0 && (
              <Select value={projectFilter} onValueChange={setProjectFilter}>
                <SelectTrigger className="h-9 w-[160px] text-sm bg-card">
                  <SelectValue placeholder="All Projects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {availableProjects.map(p => (
                    <SelectItem key={String(p.id)} value={String(p.id)}>
                      {p.code ? `${p.code} – ${p.name}` : p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Role */}
            {availableRoles.length > 0 && (
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="h-9 w-[145px] text-sm bg-card">
                  <SelectValue placeholder="All Roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  {availableRoles.map(r => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Skills */}
            {availableSkills.length > 0 && (
              <div className="w-[165px]">
                <MultiSelect
                  options={availableSkills}
                  placeholder="All Skills"
                  value={skillsFilter}
                  onValueChange={setSkillsFilter}
                  emptyMessage="No skills found."
                />
              </div>
            )}

            {/* Booking type */}
            {availableBookingTypes.length > 1 && (
              <>
                <Select value={bookingTypeFilter} onValueChange={setBookingTypeFilter}>
                  <SelectTrigger className="h-9 w-[130px] text-sm bg-card">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {availableBookingTypes.map(bt => (
                      <SelectItem key={String(bt.id)} value={String(bt.id)}>{bt.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            )}

            {/* ── Divider + icon action buttons ── */}
            <div className="h-6 w-px bg-border shrink-0" />

            <TooltipProvider delayDuration={300}>
              {/* Refresh unallocated bookings */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 shrink-0"
                    onClick={() => { void handleRefresh(); }}
                    disabled={!isLive || isRefreshing}
                    aria-label="Refresh unallocated bookings"
                  >
                    <RefreshCw className={cn('h-4 w-4', isRefreshing && 'animate-spin')} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {isLive ? 'Refresh unallocated bookings' : 'Refresh is unavailable in demo data'}
                </TooltipContent>
              </Tooltip>

              {/* Export to Excel */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 shrink-0"
                    onClick={handleExportToExcel}
                    disabled={filteredBookings.length === 0}
                    aria-label="Export to Excel"
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Export to Excel</TooltipContent>
              </Tooltip>

              {/* Configure Columns */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="relative h-9 w-9 shrink-0"
                    onClick={() => setConfigOpen(true)}
                    aria-label="Configure columns"
                  >
                    <SlidersHorizontal className="h-4 w-4" />
                    {visibleCount < ALL_COLUMNS.length && (
                      <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground leading-none">
                        {visibleCount}
                      </span>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Configure columns ({visibleCount} of {ALL_COLUMNS.length} visible)</TooltipContent>
              </Tooltip>
            </TooltipProvider>

          </div>

          {/* Active filter chips */}
          {activeFilters.length > 0 && (
            <div className="flex items-center flex-wrap gap-2">
              {activeFilters.map(f => (
                <span
                  key={f.key}
                  className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-0.5 rounded-full text-xs font-medium bg-secondary text-secondary-foreground border border-border"
                >
                  {f.label}
                  <button
                    onClick={f.clear}
                    className="ml-0.5 rounded-full p-0.5 hover:bg-muted-foreground/20 transition-colors"
                    aria-label={`Remove ${f.label} filter`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              {activeFilters.length > 1 && (
                <button
                  onClick={clearAllFilters}
                  className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
                >
                  Clear all
                </button>
              )}
            </div>
          )}
        </div>

        {/* ── Toolbar — select + results count ── */}
        <div className="flex items-center gap-2">
          <div
            className="flex items-center gap-2 border rounded-lg px-3 h-9 bg-card hover:shadow-sm hover:border-border/80 transition-all duration-150 cursor-pointer"
            onClick={() => allSelected ? clearAll() : selectAll()}
          >
            <Checkbox
              id="select-all"
              checked={allSelected}
              onCheckedChange={checked => checked ? selectAll() : clearAll()}
              onClick={e => e.stopPropagation()}
            />
            <Label htmlFor="select-all" className="text-sm font-normal cursor-pointer text-muted-foreground select-none">
              {allSelected ? 'Deselect all' : 'Select all'}
            </Label>
            {selectedIds.size > 0 && (
              <Badge variant="secondary" className="text-xs px-2 ml-1">{selectedIds.size}</Badge>
            )}
          </div>

          <div className="ml-auto flex items-center gap-1.5 text-sm text-muted-foreground">
            <span className="font-semibold text-foreground tabular-nums">{filteredBookings.length}</span>
            <span>of</span>
            <span className="font-semibold text-foreground tabular-nums">{unallocatedBookings.length}</span>
            <span>booking{unallocatedBookings.length !== 1 ? 's' : ''}</span>
            {activeFilters.length > 0 && (
              <button
                onClick={clearAllFilters}
                className="ml-2 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="h-3.5 w-3.5" />
                Clear filters
              </button>
            )}
          </div>
        </div>

        {/* ── Empty filtered state ── */}
        {filteredBookings.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center rounded-xl border border-dashed">
            <Inbox className="h-10 w-10 text-muted-foreground/30 mb-3" />
            <p className="text-sm font-medium text-muted-foreground">No bookings match your filters</p>
            <p className="text-xs text-muted-foreground/70 mt-0.5 mb-3">Try adjusting or clearing your active filters</p>
            <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={clearAllFilters}>
              <X className="h-3.5 w-3.5" />
              Clear all filters
            </Button>
          </div>

        ) : (

          /* ─────────────────── TABLE VIEW ─────────────────── */
          <div className={TABLE_CONTAINER_CLASS}>
            <Table>
              <TableHeader>
                <TableRow className={TABLE_HEADER_ROW_CLASS}>
                  {/* Checkbox — always visible */}
                  <TableHead className="w-8 pl-3 pr-0" />

                  {columnOrder.filter(k => col(k)).map(key => {
                    const sh = (field: SortField, label: string, className: string) => (
                      <SortableHead key={key} field={field} label={label} sortField={sortField} sortDir={sortDir} onSort={handleSort} className={className} />
                    );
                    if (key === 'urgency')   return sh('urgency',   'Urgency',    'w-24');
                    if (key === 'booking')   return sh('project',   'Project',    'min-w-[160px]');
                    if (key === 'date')      return sh('date',      'Date',       'w-[130px]');
                    if (key === 'role')      return sh('role',      'Role',       'w-[140px]');
                    if (key === 'skills')    return sh('skills',    'Skills',     'w-[220px]');
                    if (key === 'location')  return sh('location',  'Location',   'w-[110px]');
                    if (key === 'type')      return sh('type',      'Type',       'w-[90px]');
                    if (key === 'bestMatch') return sh('bestMatch', 'Best Match', 'w-[280px]');
                    return null;
                  })}

                  {/* Actions — always visible */}
                  <TableHead className="w-[110px] pr-3" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedBookings.map((booking, idx) => (
                  <UnallocatedTableRow
                    key={booking.id}
                    rowIndex={idx}
                    booking={booking}
                    bookingTypes={settings.bookingTypes}
                    resources={isLive ? liveData.resources.filter(isAssignableResource) : resources.filter(isAssignableResource)}
                    existingBookings={allocatedBookings}
                    projects={projects}
                    onAISuggest={handleSuggest}
                    onViewInPlanner={handleViewInPlanner}
                    onDelete={handleDeleteBooking}
                    canDelete={userCanDeleteBooking}
                    isSelected={selectedIds.has(booking.id)}
                    onToggleSelect={toggleSelect}
                    visibleColumns={visibleColumns}
                    columnOrder={columnOrder}
                  />
                ))}
              </TableBody>
            </Table>
          </div>

        )}

        {filteredBookings.length > 0 && (
          <TablePagination
            page={page}
            pageSize={pageSize}
            totalItems={filteredBookings.length}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            itemLabel="bookings"
          />
        )}

            </>
            )}
        </div>{/* end unallocated section */}
      </div>

      {/* ── Dialogs ── */}
      {singleSuggestBooking && (
        <ResourceRecommendationDialog
          open={singleSuggestOpen}
          onOpenChange={setSingleSuggestOpen}
          startDate={singleSuggestStart}
          endDate={singleSuggestEnd}
          requiredRole={singleSuggestBooking.requiredRole}
          location={singleSuggestBooking.location}
          requiredSkills={singleSuggestBooking.requiredSkills ?? []}
          projectId={singleSuggestBooking.projectId}
          projects={projects}
          resources={isLive ? liveData.resources.filter(isAssignableResource) : resources.filter(isAssignableResource)}
          existingBookings={allocatedBookings}
          currentResourceIds={[]}
          onConfirm={handleSingleConfirm}
        />
      )}
      {/* Delete Booking Confirmation Dialog — shared with the planner */}
      <DeleteBookingConfirmDialog
        open={bookingToDelete !== null}
        booking={bookingToDelete}
        onConfirm={() => { void handleConfirmDelete(); }}
        onCancel={handleCancelDelete}
      />

      {/* Bulk Delete Confirmation Dialog — shared with the planner */}
      <DeleteBookingsConfirmDialog
        open={bookingsToDelete.length > 0}
        bookings={bookingsToDelete}
        onConfirm={() => { void handleConfirmBulkDelete(); }}
        onCancel={handleCancelBulkDelete}
      />

      <BatchSuggestModal
        open={batchSuggestOpen}
        onOpenChange={setBatchSuggestOpen}
        bookings={bookingsForBatch}
        resources={isLive ? liveData.resources : resources}
        existingBookings={allocatedBookings}
        projects={projects}
        onConfirm={handleBatchConfirm}
      />

      {/* ── Configure Columns Sheet ── */}
      <Sheet open={configOpen} onOpenChange={setConfigOpen}>
        <SheetContent
          side="right"
          className="w-[440px] sm:max-w-[440px] flex flex-col p-0 gap-0"
        >
          {/* ── Header ── */}
          <SheetHeader className="px-6 pt-6 pb-5 border-b shrink-0">
            <div className="flex items-center gap-3 pr-8">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 shrink-0">
                <Settings2 className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <SheetTitle className="text-base font-semibold leading-tight">
                  Customise Columns
                </SheetTitle>
                <SheetDescription className="text-xs mt-0.5 leading-relaxed">
                  Toggle columns on or off, then drag to reorder them.
                </SheetDescription>
              </div>
            </div>

            {/* Visible count + reset row */}
            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold tabular-nums shrink-0">
                  {visibleCount}
                </div>
                <span className="text-sm text-muted-foreground">
                  of {ALL_COLUMNS.length} columns showing
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-foreground"
                onClick={resetColumns}
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Reset defaults
              </Button>
            </div>
          </SheetHeader>

          {/* ── Drag hint ── */}
          <div className="px-6 py-3 bg-muted/30 border-b shrink-0">
            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
              <GripVertical className="h-3.5 w-3.5 shrink-0" />
              Drag the handle on any row to reorder columns
            </p>
          </div>

          {/* ── Column list ── */}
          <ScrollArea className="flex-1 min-h-0">
            <div className="px-4 py-4">
              <DndContext
                sensors={dnsSensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={columnOrder} strategy={verticalListSortingStrategy}>
                  <div className="space-y-2">
                    {columnOrder.map(key => {
                      const colDef = ALL_COLUMNS.find(c => c.key === key);
                      if (!colDef) return null;
                      return (
                        <SortableColumnRow
                          key={key}
                          colDef={colDef}
                          isVisible={visibleColumns.has(key)}
                          onToggle={toggleColumn}
                        />
                      );
                    })}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          </ScrollArea>

          {/* ── Footer ── */}
          <div className="border-t px-6 py-4 shrink-0 flex items-center justify-between gap-3 bg-background">
            <p className="text-xs text-muted-foreground">
              Changes apply live to the table
            </p>
            <Button
              size="sm"
              className="gap-1.5 px-5"
              onClick={() => setConfigOpen(false)}
            >
              <Check className="h-3.5 w-3.5" />
              Done
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
